import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Button, CircularProgress } from "@mui/material";
import { linko } from "@shared/linko";
import { Roles } from "@shared/Roles";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FC } from "react";
import { remult } from "remult";
import useQuestion from "../../dialog-components/QuestionDialog";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import { invalidateOpportunityHandlingQueries } from "../../queries/invalidate-opportunity-handling";
import {
  HANDLING_COPY,
  handlingActionButtonSx,
} from "./handling-ui";
import { OpportunityHandlingStatusPill } from "./OpportunityHandlingStatusPill";

export type OpportunityHandlingControlsVariant =
  | "default"
  | "compact"
  | "header";

interface OpportunityHandlingControlsProps {
  opportunityId: string;
  handlingManagerId?: string | null;
  handlingManagerName?: string | null;
  handlingSince?: Date | string | null;
  /** @deprecated Prefer `variant="compact"` */
  compact?: boolean;
  variant?: OpportunityHandlingControlsVariant;
  onChanged?: () => void;
}

export const OpportunityHandlingControls: FC<
  OpportunityHandlingControlsProps
> = ({
  opportunityId,
  handlingManagerId,
  handlingManagerName,
  handlingSince,
  compact = false,
  variant: variantProp,
  onChanged,
}) => {
  const variant: OpportunityHandlingControlsVariant =
    variantProp ?? (compact ? "compact" : "default");

  const { currentUser } = useUser();
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();
  const question = useQuestion();

  const isMine =
    !!handlingManagerId && handlingManagerId === currentUser?.id;
  const isHandledByOther =
    !!handlingManagerId && handlingManagerId !== currentUser?.id;
  const canReleaseOthers = remult.isAllowed(Roles.Admins);
  const pillSize =
    variant === "compact" || variant === "header" ? "sm" : "md";

  const invalidate = async () => {
    await invalidateOpportunityHandlingQueries(queryClient, opportunityId);
    onChanged?.();
  };

  const claimMutation = useMutation({
    mutationFn: () =>
      linko.opportunity.mutations.claimForHandling({ opportunityId }),
    onSuccess: async (res) => {
      if (!res.data.success) {
        setSeverity("warning");
        setAlertText(res.data.message);
        return;
      }
      setSeverity("success");
      setAlertText(res.data.message);
      await invalidate();
    },
    onError: () => {
      setSeverity("error");
      setAlertText(HANDLING_COPY.claimError);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () =>
      linko.opportunity.mutations.releaseHandling({ opportunityId }),
    onSuccess: async (res) => {
      setSeverity("success");
      setAlertText(res.data.message);
      await invalidate();
    },
    onError: (err: unknown) => {
      setSeverity("error");
      const message = isAxiosTypedLinkoError(err)
        ? err.linkoError.message
        : HANDLING_COPY.releaseError;
      setAlertText(message);
    },
  });

  const isPending = claimMutation.isPending || releaseMutation.isPending;

  const handleRelease = async () => {
    if (isHandledByOther) {
      const confirmed = await question(
        HANDLING_COPY.releaseOtherConfirm(
          handlingManagerName ?? "מנהל אחר",
        ),
        "",
        { confirmText: "שחרר", cancelText: "ביטול" },
      );
      if (!confirmed) return;
    }
    releaseMutation.mutate();
  };

  const claimButton = (
    <Button
      size="small"
      variant="outlined"
      color="secondary"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation();
        claimMutation.mutate();
      }}
      startIcon={
        isPending ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <PersonOutlineIcon fontSize="small" />
        )
      }
      sx={handlingActionButtonSx}
    >
      {HANDLING_COPY.claimAction}
    </Button>
  );

  if (isHandledByOther) {
    return (
      <OpportunityHandlingStatusPill
        tone="other"
        managerName={handlingManagerName}
        handlingSince={handlingSince}
        size={pillSize}
        showRelease={canReleaseOthers}
        releasePending={isPending}
        onRelease={() => void handleRelease()}
      />
    );
  }

  if (isMine) {
    return (
      <OpportunityHandlingStatusPill
        tone="mine"
        handlingSince={handlingSince}
        size={pillSize}
        showRelease
        releasePending={isPending}
        onRelease={() => void handleRelease()}
      />
    );
  }

  return claimButton;
};

/** @deprecated Use OpportunityHandlingStatusPill — kept for external imports */
export const OpportunityHandlingBanner: FC<{
  managerName?: string | null;
  handlingSince?: Date | string | null;
  owned?: boolean;
}> = ({ managerName, handlingSince, owned = false }) => (
  <OpportunityHandlingStatusPill
    tone={owned ? "mine" : "other"}
    managerName={managerName}
    handlingSince={handlingSince}
    size="md"
  />
);
